import React, { useState } from 'react';
import { areScreeningQuestionsValid } from '../ScreeningQuestions/validation';
import { Button } from 'aha-components';
import GA from 'react-ga4';
import toast from 'react-hot-toast';
import { isMobile, getOrderSource } from '@/biz/tool';
import { useOrderListStore } from '@/hooks/useOrderListStore';
import { ProductType, ProductPhysicalSelectEnum } from '../../config';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import {
  buildCreatorScheduleConflictPayload,
  hasSubmittedScheduleConflict,
  isCreatorScheduleConflictValid
} from '@/utils/creatorScheduleConflict';
import axiosInstance from '@/apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import BonusTip from '../BonusTip';
import PriceDetails from '../PriceDetails';
import { scrollElementToTop } from '@/utils/scrollElementToTop';
import { AudienceViewSource, SelfEstimatedStatus } from './config';
import { getEstimatedRateLevel } from '../EstimatePriceComponents/rateValidation';
import CreatorScheduleConflictSummary from '../CreatorScheduleConflictSummary';

// 弹窗右侧  价格详情 & 提交按钮

const PriceContent: React.FC<{
  step?: number;
  setStep?: (v: number) => void;
  setSelectCountryError: (v: boolean) => void;
  setAudienceError: (v: boolean) => void;
  isPriceValid: boolean;
}> = ({
  setSelectCountryError,
  setAudienceError,
  isPriceValid,
  step = 0,
  setStep = () => {}
}) => {
  const { t } = useTranslation();
  const {
    setShowModalMissOut,
    setShowModalTakeOrder,
    agreeProductShip,
    productShippingCountry,
    showModalAcceptAhaPicksOffer,
    setModalNotMatchInfo,
    setShowModalSelectInvitedOrderByNoMatch,
    setShowModalSelectInvitedOrderByNoMatchType,
    setEducationModal,
    skipScript,
    takeOrderAudienceView
  } = useWebModalStore();

  const { detailData, reQueryDetailData } = useDetailDataStore();
  const { reQueryOrderListData } = useOrderListStore();
  const {
    rateNonNegotiable,
    setCompetitivePriceDetail,
    competitivePriceDetail,
    screeningQuestions,
    setScreeningQuestionsError,
    creatorLowActualGet,
    creatorHighActualGet,
    newOrderPrice,
    triggerPriceErrorShake,
    creatorScheduleConflict
  } = useMultiplePricesStore();
  const isRateSubmissionValid =
    showModalAcceptAhaPicksOffer ||
    (isPriceValid &&
      getEstimatedRateLevel(
        newOrderPrice,
        creatorLowActualGet,
        creatorHighActualGet
      ) !== 'error');
  const [joinBtnLoading, setJoinBtnLoading] = useState(false);
  const order = detailData?.order;
  const scheduleConflictSubmitted = hasSubmittedScheduleConflict(
    order?.scheduleConflict
  );
  const { payment, product } = detailData;
  const isProductShip =
    product?.productType === ProductType.PHYSICAL_PRODUCT &&
    product?.physicalProductShipType === ProductPhysicalSelectEnum.ship;

  const joinCampaign = async () => {
    if (
      !isRateSubmissionValid ||
      (!scheduleConflictSubmitted &&
        !isCreatorScheduleConflictValid(creatorScheduleConflict))
    )
      return;
    const orderId = order.id;
    const campaignId = order.campaignId;
    const platform = order.platform;
    setJoinBtnLoading(true);
    const isAhaPicksOffer = !!order?.ahaPicksOffer;
    try {
      const params: any = {
        campaignId,
        platform,
        orderId
      };
      // 使用改价后获取到的价格详情进行提交
      if (competitivePriceDetail) {
        params.interestedPriceFields = [competitivePriceDetail];
      }
      if (skipScript) {
        params.skipScript = true;
      }
      if (productShippingCountry) {
        params.productShippingCountry = productShippingCountry;
      }
      params.nonNegotiable = rateNonNegotiable;
      // 后续报价沿用 order 中首次提交的数据，避免默认 No 覆盖已有档期。
      if (!scheduleConflictSubmitted) {
        params.scheduleConflict = buildCreatorScheduleConflictPayload(
          creatorScheduleConflict
        );
      }
      if (screeningQuestions?.length > 0) {
        params.screeningQuestions = screeningQuestions;
      }
      const { data } = isAhaPicksOffer
        ? await axiosInstance.post(`/order/${orderId}/ahaPicksOffer/respond`, {
            action: showModalAcceptAhaPicksOffer ? 'accept' : 'counter',
            ...params
          })
        : await axiosInstance.post(`/campaign/join`, params);
      if (
        !data?.data?.success &&
        data?.data?.message &&
        data?.data?.message === 'not_match'
      ) {
        setShowModalTakeOrder(false);
        setCompetitivePriceDetail(null);
        setModalNotMatchInfo({
          visible: true,
          message: data?.data?.detail
        });
        return;
      }
      const orderSource = getOrderSource(orderId);
      GA.gtag('event', 'join_campaign', {
        order_id: orderId,
        campaign_id: campaignId,
        order_source: orderSource,
        category: platform,
        isAhaPicksOffer: isAhaPicksOffer ? 1 : 0
      });
      setShowModalTakeOrder(false);
      setCompetitivePriceDetail(null);
      if (data?.data?.isFirstOrder) {
        await reQueryDetailData({ orderId });
        setEducationModal(true, detailData);
        return;
      }
      toast.success(
        t('campaign.modal.submit_interest.success_message', {
          defaultValue: 'You have successfully applied!'
        }),
        { duration: 2000 }
      );
      setShowModalSelectInvitedOrderByNoMatchType('success');
      setShowModalSelectInvitedOrderByNoMatch(true);
      await reQueryOrderListData();
      if (payment) {
        reQueryDetailData({ orderId });
      } else {
        setTimeout(() => {
          window.location.href = `/campaign/wallet/${orderId}`;
        }, 2000);
      }
    } catch (error: any) {
      if (error.code === 40010) {
        toast.error(
          t('campaign.modal.submit_interest.quota_full_error', {
            defaultValue:
              'We sincerely apologize, but this campaign has reached its capacity and no longer has available spots. Thank you for your understanding!'
          })
        );
        reQueryDetailData({ orderId });
      } else {
        toast.error(error.message);
      }
      console.error(`Failed to fetch : (/campaign/join) \n`, error);
    } finally {
      setJoinBtnLoading(false);
    }
  };

  // 判断当前订单有没有受众信息（优先使用弹窗内轮询/获取到的数据）
  const audienceView = takeOrderAudienceView ?? detailData?.audienceView;
  const hasAudienceData =
    audienceView?.source === AudienceViewSource.FIRST_PARTY ||
    (audienceView?.source === AudienceViewSource.SELF_ESTIMATED &&
      audienceView?.status === SelfEstimatedStatus.SUCCESS);

  const handleSubmit = () => {
    if (joinBtnLoading) return;
    if (!isRateSubmissionValid) {
      triggerPriceErrorShake();
      return;
    }
    if (!agreeProductShip && isProductShip) {
      setSelectCountryError(true);
      scrollElementToTop('take-order-shipping-country');
      return;
    }
    if (!hasAudienceData) {
      setAudienceError(true);
      scrollElementToTop('take-order-audience-demographics');
      return;
    }
    if (!areScreeningQuestionsValid(screeningQuestions)) {
      setScreeningQuestionsError(true);
      scrollElementToTop('take-order-screening-questions');
      return;
    }
    setScreeningQuestionsError(false);
    setAudienceError(false);
    joinCampaign();
  };

  return (
    <div
      className={`${isMobile() ? 'w-full px-0' : 'w-[410px] shrink-0 pl-6'} py-4 flex flex-col items-start gap-4 min-h-0`}
    >
      <div className="text-[#0D0D0D] text-base font-semibold font-['Plus Jakarta Sans'] leading-snug">
        {t('modal.modal_take_order.price_content_title', {
          defaultValue: 'Pricing details'
        })}{' '}
      </div>
      <PriceDetails order={order} product={product} />
      <CreatorScheduleConflictSummary />
      {/* bonus提示 */}
      <BonusTip order={order} isOffering={true} />
      {/* 提交按钮 */}
      <div
        className={`w-full flex items-center gap-4 ${step === 3 ? 'flex-row' : 'flex-col'}`}
      >
        {step === 3 && (
          <Button
            className="!w-full !h-10"
            type="default"
            onClick={() => {
              setStep(step - 1);
            }}
          >
            {t('general_text.back', { defaultValue: 'Back' })}
          </Button>
        )}
        <Button
          className="!w-full !h-10"
          type="primary"
          onClick={handleSubmit}
          loading={joinBtnLoading}
          disabled={joinBtnLoading}
        >
          {t('modal.modal_take_order.title', {
            defaultValue: 'Submit interest'
          })}
        </Button>
        {step !== 3 && (
          <Button
            className="!w-full !h-10"
            type="default"
            onClick={() => {
              setShowModalTakeOrder(false);
              setCompetitivePriceDetail(null);
              setShowModalMissOut(true);
            }}
          >
            {t('general_text.cancel', { defaultValue: 'Cancel' })}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PriceContent;
